import { getMoveSectionTargetIndex } from "redux/actions/websiteBuilder/editor/actions/section/moveSection";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { websiteBuilderUpdateSelectedNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  IWebsiteBuilderContextMenuPosition,
  TWebsiteBuilderContextMenuSection,
  WebsiteBuilderContextMenu,
} from "../../helpers/WebsiteBuilderContextMenu";
import {
  TWebsiteBuilderSection,
  TWebsiteBuilderSectionType,
} from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";

export interface IUseSectionViewContextMenuArgs {
  node: TWebsiteBuilderSection;
  root: IWebsiteBuilderRoot;
  type: TWebsiteBuilderSectionType;
  selected: boolean;
  menuActive: boolean;
  openSettings: () => void;
}

interface ISectionViewContextMenuState {
  position: IWebsiteBuilderContextMenuPosition;
}

const initialState: ISectionViewContextMenuState = {
  position: null,
};

interface IUseSectionViewContextMenu {
  viewContextMenu: JSX.Element;
  handleViewOpenContextMenu: (position: IWebsiteBuilderContextMenuPosition) => void;
}

export function useSectionViewContextMenu(args: IUseSectionViewContextMenuArgs): IUseSectionViewContextMenu {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { activePageDomNodeRefMap, scrollContainerRef, sectionOperations, clipboard } = useWebsiteBuilderEditorContext();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);

  const [state, setState] = useState<ISectionViewContextMenuState>(cloneDeep(initialState));

  const modifierKey = window.navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";

  const headerSection = args.type === "header";
  const footerSection = args.type === "footer";

  const outerSection = headerSection || footerSection;

  useEffect(() => {
    if (!args.menuActive) {
      setState(cloneDeep(initialState));
    }
  }, [args.menuActive]);

  const { canMoveUp, canMoveDown } = useMemo(() => {
    if (outerSection) {
      return { canMoveUp: false, canMoveDown: false };
    }

    const index = args.root.children.findIndex(child => child.id === args.node.id);

    return {
      canMoveUp: getMoveSectionTargetIndex(index, "up", args.root.children, deviceView) !== -1,
      canMoveDown: getMoveSectionTargetIndex(index, "down", args.root.children, deviceView) !== -1,
    };
  }, [outerSection, args.root.children, args.node.id, deviceView]);

  const settingsMenuSection: TWebsiteBuilderContextMenuSection = [{
    label: t("Settings"),
    shortcut: "",
    onClick: args.openSettings,
    skipCloseAfterClick: true,
  }];

  const moveSectionMenuSection: TWebsiteBuilderContextMenuSection = [
    {
      label: t("Move Section Up"),
      shortcut: "",
      onClick: () =>
        sectionOperations.moveSectionUp(args.node, args.root, activePageId, deviceView, activePageDomNodeRefMap, scrollContainerRef),
      disabled: !canMoveUp,
    },
    {
      label: t("Move Section Down"),
      shortcut: "",
      onClick: () =>
        sectionOperations.moveSectionDown(args.node, args.root, activePageId, deviceView, activePageDomNodeRefMap, scrollContainerRef),
      disabled: !canMoveDown,
    },
  ];

  const creationMenuSection: TWebsiteBuilderContextMenuSection = [
    {
      label: t("Copy"),
      shortcut: `${modifierKey} + C`,
      onClick: () => sectionOperations.copySection(args.node),
      disabled: outerSection,
    },
    {
      label: t("Paste"),
      shortcut: `${modifierKey} + V`,
      onClick: () =>
        sectionOperations.pasteAtSection(
          args.node,
          args.root,
          activePageId,
          deviceView,
          activePageDomNodeRefMap,
          scrollContainerRef,
        ),
      disabled: clipboard.copyEmpty,
    },
    {
      label: t("Duplicate"),
      shortcut: `${modifierKey} + D`,
      onClick: () => sectionOperations.duplicateSection(args.node, args.root, activePageId, deviceView, activePageDomNodeRefMap),
      disabled: outerSection,
    },
  ];

  const deletionMenuSection: TWebsiteBuilderContextMenuSection = [
    {
      label: t("Delete"),
      shortcut: "Del",
      onClick: () => sectionOperations.deleteSection(args.node, args.root, activePageId, deviceView),
      disabled: outerSection,
    },
    {
      label: t("Cut"),
      shortcut: `${modifierKey} + X`,
      onClick: () => sectionOperations.cutSection(args.node, args.root, activePageId, deviceView),
      disabled: outerSection,
    },
  ];

  function closeMenu() {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: "section",
          editingMode: {
            type: "none",
          },
        },
      }),
    );
  }

  function handleViewOpenContextMenu(position: IWebsiteBuilderContextMenuPosition) {
    if (!args.selected) {
      return;
    }

    if (!args.menuActive) {
      dispatch(
        websiteBuilderUpdateSelectedNode({
          selectedNodeUpdates: {
            type: "section",
            editingMode: {
              type: "section-context-menu",
            },
          },
        }),
      );
    }

    setState(prev => ({
      ...prev,
      position,
    }));
  }

  function renderContextMenu() {
    if (!args.menuActive || state.position == null) {
      return null;
    } else {
      return (
        <WebsiteBuilderContextMenu
          position={state.position}
          sections={[settingsMenuSection, moveSectionMenuSection, creationMenuSection, deletionMenuSection]}
          closeMenu={closeMenu}
        />
      );
    }
  }

  return {
    viewContextMenu: renderContextMenu(),
    handleViewOpenContextMenu,
  };
}
